import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable } from "../../v3";
import { PurchaseService } from "../../../services";
import { getMaterialTableColumns } from "../../../pages/library/materials/constants";
import { AddEditMaterial } from "./AddEditMaterial.component";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../../utils";
import { DELETE_MATERIAL, EDIT_MATERIAL } from "../../../constants";
import { handlePdfDownload, Listing } from "../../pdf";

export const MaterialList = ({ totalCount, setTotalCount, onSuccess = () => {}, reloadData = 0 }) => {
  const [materials, setMaterials] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [materialId, setMaterialId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: "updatedAt", sort: "desc" }]);
  const [paginationState, setPaginationState] = useState({
    page: 0,
    pageSize: 10,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    materialId: null,
  });

  const { user } = useSelector((state) => state.user);

  const hasEditMaterial = useHasPermission(EDIT_MATERIAL);
  const hasDeleteMaterial = useHasPermission(DELETE_MATERIAL);

  const fetchData = useCallback(
    async (materialFilters = {}) => {
      try {
        setLoading(true);
        const response = await PurchaseService.getPurchaseItems(materialFilters);
        setMaterials(response?.data?.rows);
        setTotalCount(response?.data?.count);
      } catch (error) {
        console.log("🚀 ~ CategoryList ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [setTotalCount],
  );

  useEffect(() => {
    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      name: searchText,
    });
  }, [fetchData, sortModel, paginationState, searchText, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title={"Materials"} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Materials",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = (materialData) => {
    setDeleteModal({
      isOpen: true,
      materialId: materialData.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, materialId: null });
  };

  const handleDelete = async () => {
    try {
      await PurchaseService.deletePurchaseItem(deleteModal.materialId);
      setDeleteModal({ isOpen: false, materialId: null });
      toast.success("Material deleted successfully");
      onSuccess?.();
    } catch (error) {
      toast.error("Failed to delete material");
      console.error("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const handleActions = (materialData, actionType) => {
    switch (actionType) {
      case "edit":
        setMaterialId(materialData?.id);
        setOpenModal(true);
        break;
      case "delete":
        openDeleteModal(materialData);
        break;
      default:
        break;
    }
  };

  const columnHeaders = getMaterialTableColumns(handleActions, hasEditMaterial, hasDeleteMaterial);

  const rows = materials.map((material, index) => ({
    sl: index + 1 + paginationState.page * paginationState.pageSize,
    id: material.id,
    name: material.name,
    description: material.description,
    taxPercentage: material.taxPercentage,
    unitOfMeasure: material.unitOfMeasure,
    defaultPrice: material.defaultPrice,
  }));

  return (
    <>
      <CardLayout>
        <MTable
          // Search data
          placeholder="Search category"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          // Export data
          showExportButton // TODO: implement permission
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
        />
      </CardLayout>

      {/* edit category modal */}
      <AddEditMaterial open={openModal} materialId={materialId} onClose={handleCloseModal} onSuccess={onSuccess} />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this material?"
      />
    </>
  );
};
